<template>
  <div class="console-page">
    <div class="console-page__header">
      <div>
        <span class="label">Workspace</span>
        <h1 class="console-page__title">{{ workspace.name }}</h1>
        <p class="console-page__placeholder">
          <span class="console-badge console-badge--small console-badge--ready">{{ workspace.status }}</span>
          <span class="console-detail__stat">{{ workspace.sourceLabel }}</span>
          <span class="console-detail__stat">{{ workspace.createdAtLabel }}</span>
        </p>
      </div>
    </div>

    <div class="console-page__demo-intro">
      <p class="console-page__intro">A workspace is a project connected to a source: a GitHub repo or an uploaded archive. When the AI runs against the workspace, it picks up the standards and corrections previous runs left behind. This is how corrections compound instead of resetting every session.</p>
      <router-link :to="{ path: '/demo/jobs/' + featuredJob.id }" class="button button--primary">View run</router-link>
    </div>

    <section class="console-panel console-panel--ready">
      <details class="console-module" open>
        <summary class="console-module__summary">Overview</summary>
        <div class="console-detail__overview">
          <div>
            <p class="console-text-block">{{ workspace.description }}</p>
            <p v-if="workspace.repoUrl" class="console-text-block">
              <a :href="workspace.repoUrl" target="_blank" rel="noopener" class="external-link">{{ workspace.repoUrl }}</a>
              <span v-if="workspace.branch"> @ {{ workspace.branch }}</span>
            </p>
            <p class="console-text-block">{{ workspace.lastSyncedLabel }}</p>
          </div>

          <div class="console-signal-strip">
            <div
              v-for="stat in workspace.stats"
              :key="stat.label"
              class="console-signal"
              :class="`console-signal--${stat.tone}`"
            >
              <span class="console-signal__label">{{ stat.label }}</span>
              <span class="console-signal__value">{{ stat.value }}</span>
              <span class="console-signal__note">{{ stat.note }}</span>
            </div>
          </div>
        </div>
      </details>

      <details class="console-module" open>
        <summary class="console-module__summary">Guidance Gaps</summary>
        <p class="console-text-block">{{ workspace.guidance.summary }}</p>
        <div class="console-tag-row">
          <span v-for="tag in workspace.guidance.gapTags" :key="tag" class="console-badge console-badge--neutral">{{ tag }}</span>
        </div>
        <div class="console-topic-list">
          <div v-for="topic in workspace.guidance.recurringTopics" :key="topic.topic" class="console-topic">
            <div class="console-topic__header">
              <strong>{{ topic.topic }}</strong>
              <span>{{ topic.count }}</span>
            </div>
            <div class="console-tag-row">
              <span v-for="tag in topic.tags" :key="tag" class="console-badge console-badge--small console-badge--neutral">{{ tag }}</span>
            </div>
          </div>
        </div>
      </details>

      <details class="console-module" open>
        <summary class="console-module__summary">Recent Runs <span class="console-module__count">{{ workspace.jobs.length }}</span></summary>
        <div class="console-stream">
          <router-link
            v-for="job in workspace.jobs"
            :key="job.id"
            :to="job.id === featuredJob.id ? { path: '/demo/jobs/' + job.id } : { path: '/demo/jobs/' + featuredJob.id }"
            class="console-stream__item"
          >
            <div>
              <p class="console-stream__title">{{ job.title }}</p>
              <p class="console-stream__meta">{{ job.updatedAt }} | {{ job.note }}</p>
            </div>
            <div class="console-stream__aside">
              <span class="console-badge console-badge--small" :class="`console-badge--${job.tone}`">{{ job.statusLabel }}</span>
            </div>
          </router-link>
        </div>
      </details>

      <details class="console-module">
        <summary class="console-module__summary">Details</summary>
        <dl class="console-meta-list">
          <div><dt>Workspace ID</dt><dd class="console-mono">{{ workspace.id }}</dd></div>
          <div><dt>Status</dt><dd>{{ workspace.status }}</dd></div>
          <div v-if="workspace.repoUrl"><dt>Repository</dt><dd class="console-mono">{{ workspace.repoUrl }}</dd></div>
          <div v-if="workspace.branch"><dt>Branch</dt><dd class="console-mono">{{ workspace.branch }}</dd></div>
        </dl>
      </details>
    </section>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { demoJob, getDemoWorkspace } from '@/config/demo'

const route = useRoute()
const workspace = computed(() => getDemoWorkspace(route.params.id) || getDemoWorkspace('captured-docs-remap'))
const featuredJob = demoJob
</script>
